import { createFileRoute, Navigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { Button, Skeleton } from "@rumbledirect/ui-v2";
import { RequirePermission } from "@/currentUser/RequirePermission";
import type {
  SelectorSelectorQueryRevisionResourceSelectorQueryRevisionOutputJsonld as Revision,
  SelectorSelectorQueryResourceQueryOutputJsonld as Query,
} from "@/lib/api/generated/model";
import {
  useGetAdminSelectorQueriesId,
  useGetAdminSelectorRevisions,
} from "@/lib/api/generated/selector/selector";
import { SelectorVersionEditor } from "@/steering/SelectorVersionEditor";
import { blankDraft, fromConfiguration } from "@/steering/selectorEditorDraft";
import { revisionLabel } from "@/steering/selectorRevisions";

interface NewRevisionSearch {
  /** The revision the new one copies; required once the query has revisions. */
  basedOn?: string;
}

export const Route = createFileRoute("/_authenticated/steering_/$queryId_/new")(
  {
    validateSearch: (search: Record<string, unknown>): NewRevisionSearch => ({
      basedOn: typeof search.basedOn === "string" ? search.basedOn : undefined,
    }),
    component: NewSelectorRevisionPage,
  },
);

function NewSelectorRevisionPage(): ReactNode {
  return (
    <RequirePermission
      permission="admin_selector_query_create"
      fallback={
        <p role="alert">Je hebt geen rechten om versies aan te maken.</p>
      }
    >
      <NewSelectorRevision />
    </RequirePermission>
  );
}

function NewSelectorRevision(): ReactNode {
  const { queryId } = Route.useParams();
  const { basedOn } = Route.useSearch();
  const query = useGetAdminSelectorQueriesId(queryId);
  const revisions = useGetAdminSelectorRevisions({ query: queryId });

  if (query.isPending || revisions.isPending)
    return (
      <div role="status">
        <span className="sr-only">Selectieregel wordt geladen…</span>
        <Skeleton className="h-9 w-72" />
      </div>
    );
  if (query.isError || revisions.isError)
    return (
      <div role="alert" className="py-6">
        <p>De selectieregel kon niet worden geladen.</p>
        <Button
          className="mt-4"
          variant="outline"
          onClick={() => {
            void query.refetch();
            void revisions.refetch();
          }}
        >
          Opnieuw proberen
        </Button>
      </div>
    );

  const members = revisions.data.member;
  const base = members.find((revision) => revision.id === basedOn);
  // Once a query has revisions, a new one always copies one of them.
  if (members.length > 0 && !base) {
    const fallback =
      members.find((revision) => revision.id === query.data.activeRevision) ??
      members.at(-1);
    return (
      <Navigate
        to="/steering/$queryId/new"
        params={{ queryId }}
        search={{ basedOn: fallback?.id }}
        replace
      />
    );
  }
  return <NewRevisionEditor key={base?.id} query={query.data} base={base} />;
}

function NewRevisionEditor({
  query,
  base,
}: {
  readonly query: Query;
  readonly base: Revision | undefined;
}): ReactNode {
  const navigate = Route.useNavigate();
  // One draft object serves as both the start and the comparison base, so
  // the change list can match items by their draft ids.
  const [draft] = useState(() =>
    base ? fromConfiguration(base.configuration) : blankDraft(),
  );
  const toDetail = (revision: string | undefined) =>
    void navigate({
      to: "/steering/$queryId",
      params: { queryId: query.id },
      search: { revision },
    });
  return (
    <SelectorVersionEditor
      queryId={query.id}
      queryName={query.name}
      badge={{ label: "Nieuwe versie", variant: "neutral" }}
      base={base ? { label: revisionLabel(base), draft } : null}
      initialDraft={draft}
      initialActivation="concept"
      initialRevisionAt={null}
      onDone={(revision) => toDetail(revision.id)}
      onCancel={() => toDetail(base?.id)}
    />
  );
}
